"use client";

import { Heading, Select, SelectItem } from "@pamperedchef/design-system";
import { useTranslations } from "next-intl";
import Script from "next/script";
import {
	type ComponentType,
	type Key,
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { createPortal } from "react-dom";

import { PR_ANCHOR_IDS } from "./power-review";
import { decorateHelpfulCounts } from "./power-review/helpful-summary";
import {
	createCustomerGallery,
	decorateReviewImages,
	placeCustomerGallery
} from "./power-review/image-gallery";
import {
	decorateFlagForm,
	decorateGalleryRating,
	decorateWriteForm
} from "./power-review/modal-decorations";
import "./power-review/power-reviews-overrides.scss";
import {
	getReviewsRatingsProvider,
	type ReviewsRatingsPayload
} from "./reviews-ratings.provider";
import * as styles from "./ReviewsRatings.module.scss";

const SORT_SELECT_ID = "pr-rd-sort-by";
const SORT_MOUNT_ATTR = "data-pr-sort-mount";

// DS 4.0.133 exports SelectItem as react-stately's Item, whose types omit the
// `label` prop — but the DS ListBox renders each option from `label` (children
// only feed the trigger's rendered value). Cast so we can pass `label` without
// a type error; runtime behaviour is unchanged.
const SortOptionItem = SelectItem as unknown as ComponentType<{
	key?: Key;
	label?: ReactNode;
	textValue?: string;
	children?: ReactNode;
}>;

interface SortOption {
	value: string;
	label: string;
}

export interface ReviewsRatingsClientProps {
	scriptUrl: string;
	payload: ReviewsRatingsPayload;
}

function safely(fn: () => void): void {
	try {
		fn();
	} catch {
		return;
	}
}

function sameOptions(a: SortOption[], b: SortOption[]): boolean {
	return (
		a.length === b.length &&
		a.every((o, i) => o.value === b[i]?.value && o.label === b[i]?.label)
	);
}

export function ReviewsRatingsClient({
	scriptUrl,
	payload
}: ReviewsRatingsClientProps): JSX.Element {
	const t = useTranslations("reviewsRatings");
	const [isScriptReady, setIsScriptReady] = useState(false);
	const providerRef = useRef(getReviewsRatingsProvider());
	const tRef = useRef(t);
	tRef.current = t;

	// PowerReviews renders a native <select> for sorting; we replace it with the
	// design-system Select (portaled into PR's DOM) and drive the native select
	// so PR still performs the actual sort.
	const [sortMount, setSortMount] = useState<HTMLElement | null>(null);
	const [sortOptions, setSortOptions] = useState<SortOption[]>([]);
	const [sortValue, setSortValue] = useState<string>("");

	const handleSortChange = useCallback((key: React.Key | null) => {
		if (key == null) return;
		const native = document.getElementById(
			SORT_SELECT_ID
		) as HTMLSelectElement | null;
		if (!native) return;
		native.value = String(key);
		native.dispatchEvent(new Event("change", { bubbles: true }));
		setSortValue(String(key));
	}, []);

	useEffect(() => {
		if (!isScriptReady) return;
		const provider = providerRef.current;
		const root = document.getElementById(PR_ANCHOR_IDS.reviewDisplay);
		const gallery = createCustomerGallery(tRef.current("customerImages"));
		root?.parentElement?.insertBefore(gallery, root);
		provider.render(payload);

		let observer: MutationObserver | undefined;
		let modalObserver: MutationObserver | undefined;
		if (root) {
			const enhance = (): void => {
				safely(() =>
					decorateHelpfulCounts(root, (count) =>
						tRef.current("helpfulCount", { count })
					)
				);
				safely(() =>
					decorateReviewImages(root, (count) =>
						tRef.current("moreImages", { count })
					)
				);
				safely(() =>
					placeCustomerGallery(root, gallery, (count) =>
						tRef.current("moreImages", { count })
					)
				);
				safely(() => decorateWriteForm(document));
				safely(() => decorateFlagForm(document));
				safely(() => decorateGalleryRating(document));
				safely(() => {
					root.querySelectorAll(".pr-snippet-review-count").forEach(
						(el) => {
							const digits =
								el.textContent?.match(/\d[\d,]*/)?.[0];
							if (!digits) return;
							const formatted = `(${digits})`;
							if (el.textContent !== formatted) {
								el.textContent = formatted;
							}
						}
					);
				});
				safely(() => {
					const group = root.querySelector(".pr-rd-sort optgroup");
					const select = group?.parentElement;
					if (group && select) {
						while (group.firstChild) {
							select.insertBefore(group.firstChild, group);
						}
						group.remove();
					}
				});
				safely(() => {
					const search = root.querySelector<HTMLInputElement>(
						".pr-rd-search-reviews-input input"
					);
					const placeholder = tRef.current("searchPlaceholder");
					if (search && search.placeholder !== placeholder) {
						search.placeholder = placeholder;
					}
				});
				safely(() => {
					// shorten PR's "of respondents would recommend this to a
					// friend" to the Figma wording
					const msg = root.querySelector<HTMLElement>(
						".pr-reco-to-friend-message"
					);
					const text = tRef.current("recommendMessage");
					if (msg && msg.textContent !== text) {
						msg.textContent = text;
					}
				});
				safely(() => {
					// Force the reviewer meta onto two lines ("By <name>" + badge /
					// "From <location>, <date>"). Flexbox has no mid-list break, so
					// inject a full-width spacer the CSS orders between the lines.
					root.querySelectorAll(".pr-rd-side-content-block").forEach(
						(block) => {
							if (
								block.querySelector(
									":scope > [data-pr-line-break]"
								)
							)
								return;
							const brk = document.createElement("span");
							brk.setAttribute("data-pr-line-break", "");
							brk.setAttribute("aria-hidden", "true");
							block.appendChild(brk);
						}
					);
				});
				safely(() => {
					const native = root.querySelector<HTMLSelectElement>(
						`#${SORT_SELECT_ID}`
					);
					const group = native?.closest(
						".pr-rd-sort-group"
					) as HTMLElement | null;
					if (!native || !group) return;

					const options: SortOption[] = Array.from(
						native.options
					).map((o) => ({
						value: o.value,
						label: o.textContent?.trim() || o.value
					}));

					// reuse our portal target across PR re-renders; recreate only
					// if PR replaced the header (removed our mount node)
					let mount = group.querySelector<HTMLElement>(
						`:scope > [${SORT_MOUNT_ATTR}]`
					);
					if (!mount) {
						mount = document.createElement("div");
						mount.setAttribute(SORT_MOUNT_ATTR, "");
						group.appendChild(mount);
					}

					setSortMount((prev) => (prev === mount ? prev : mount));
					setSortOptions((prev) =>
						sameOptions(prev, options) ? prev : options
					);
					setSortValue((prev) =>
						prev === native.value ? prev : native.value
					);
				});
			};
			enhance();
			observer = new MutationObserver(enhance);
			observer.observe(root, { childList: true, subtree: true });

			modalObserver = new MutationObserver(() => {
				safely(() => decorateWriteForm(document));
				safely(() => decorateFlagForm(document));
				safely(() => decorateGalleryRating(document));
			});
			modalObserver.observe(document.body, {
				childList: true,
				subtree: true
			});
		}

		return () => {
			observer?.disconnect();
			modalObserver?.disconnect();
			provider.teardown();
			gallery.remove();
		};
	}, [isScriptReady, payload]);

	return (
		<section
			id="reviews"
			className={styles?.["reviewsRatings"]}
			aria-label="Reviews and Ratings"
		>
			<Heading tag="h2" className={styles?.["title"]}>
				{t("title")}
			</Heading>
			<Script
				src={scriptUrl}
				strategy="afterInteractive"
				onReady={() => setIsScriptReady(true)}
				onLoad={() => setIsScriptReady(true)}
			/>
			<div id={PR_ANCHOR_IDS.reviewDisplay} data-sku={payload.sku} />
			<div id={PR_ANCHOR_IDS.questionDisplay} data-sku={payload.sku} />
			{sortMount &&
				sortOptions.length > 0 &&
				createPortal(
					<Select
						aria-label={t("sortLabel")}
						className="pr-sort-ds"
						selectedKey={sortValue}
						onSelectionChange={handleSortChange}
						placeholderText={sortOptions[0]?.label}
					>
						{sortOptions.map((option) => (
							<SortOptionItem
								key={option.value}
								label={option.label}
								textValue={option.label}
							>
								{option.label}
							</SortOptionItem>
						))}
					</Select>,
					sortMount
				)}
		</section>
	);
}
